import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props { children: ReactNode }
interface State { hasError: boolean; error: Error | null; componentStack: string | null }

// Top-level error boundary — catches render crashes that would otherwise
// break the whole page via React 19 default reportError. Displays the error
// message + component stack so the crashing component is identifiable.
export class RootErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    // Also log to console for copy-paste
    console.error("[RootErrorBoundary]", error, "\nComponent stack:", info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, fontFamily: "monospace", fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
          <h2 style={{ color: "#c00", marginBottom: 8 }}>Render error caught</h2>
          <p><strong>Error:</strong> {this.state.error?.message ?? "unknown"}</p>
          <p><strong>Stack:</strong></p>
          <pre>{this.state.error?.stack}</pre>
          <p><strong>Component stack:</strong></p>
          <pre>{this.state.componentStack}</pre>
          <button
            onClick={() => this.setState({ hasError: false, error: null, componentStack: null })}
            style={{ marginTop: 12, padding: "6px 12px", cursor: "pointer" }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
